import {
  App,
  Button,
  Flex,
  Space,
  Table,
} from 'antd'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { FeatureKey } from '@/constants/feature-key'
import { NormalStatus } from '@/constants/normal-status'
import { Pagination, PaginationQuery } from '@/constants/pagination'
import { Employee } from '@/model/employee'
import { Role } from '@/model/role'
import { readAllEmployees } from '@/redux/employee-actions'
import {
  readAllRoles,
  removeRole,
  SearchRolesQuery,
  updateRole,
} from '@/redux/role'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import styles from '../settings.module.css'

import SearchRole from './components/search-role'
import useRoleEditModal from './hooks/use-role-edit-modal'
import useRoleSettingsColumns, { TableData } from './hooks/use-role-settings-columns'
import SetPermissions from './set-permissions'


function roleToTableData(employees: Employee[], role: Role): TableData {
  const employeeNames = role.employeeIds
    .map((id) => employees.find((e) => e.id === id)?.name || '')
    .filter(name => name !== '')

  return {
    key: role.id,
    name: role.name,
    description: role.description,
    users: employeeNames,
    status: role.status,
    disabled: role.isSystemDefault,
  }
}

export const selectManageEmployees = createAppSelector([
  (state) => state.employee.employees,
], (employees) => employees[FeatureKey.ManageEmployees])

export default function RoleSettings() {
  const [pagingRoles, setPagingRoles] = useState<Pagination<Role>>()
  const [pagingQuery, setPagingQuery] = useState<PaginationQuery>()
  const [loading, setLoading] = useState(false)
  const [roleInSetting, setRoleInSetting] = useState<Role | null>()
  const { message, modal } = App.useApp()

  const employees = useAppSelector(selectManageEmployees)
  const dispatch = useAppDispatch()

  useEffect(() => {
    void dispatch(readAllEmployees({ permissionKey: FeatureKey.ManageEmployees }))
  }, [dispatch])

  const roles = useMemo(() => pagingRoles?.rows || [], [pagingRoles])
  const { createFormModal, openCreateModal, updateFormModal, openUpdateModal } =
    useRoleEditModal({
      roles,
      async onCreateOrUpdate() {
        await doSearchRoles()
      },
    })

  const doSearchRoles = useMemo(() => {
    return async (values?: SearchRolesQuery) => {
      setLoading(true)
      const result = await dispatch(
        readAllRoles({ ...values, ...pagingQuery }),
      ).unwrap()
      setPagingRoles(result)
      setLoading(false)
    }
  }, [dispatch, pagingQuery])

  useAsync(async () => {
    await doSearchRoles()
  }, [pagingQuery, doSearchRoles])

  const tableData = useMemo(() => {
    return roles.map((role) => roleToTableData(employees, role))
  }, [employees, roles])

  const onStatusChange = useCallback(
    async function (value: string | string[] | boolean | undefined, record: TableData) {
      setLoading(true)
      const role = roles.find((role) => role.id === record.key)
      if (!role) return

      await dispatch(
        updateRole({
          id: role.id,
          name: record.name,
          status: value as NormalStatus,
        }),
      ).unwrap()
      await doSearchRoles()
      void message.success('Role updated successfully')
      setLoading(false)
    },
    [dispatch, roles, message, doSearchRoles],
  )

  const onEditClick = function (record: TableData) {
    openUpdateModal({
      id: record.key,
      name: record.name,
      description: record.description,
    })
  }

  const onDeleteClick = useCallback(
    async function (record: TableData) {
      await modal.confirm({
        title: 'Delete Role',
        content: `Are you sure you want to delete the role "${record.name}"?`,
        centered: true,
        icon: null,
        async onOk() {
          await dispatch(removeRole(record.key)).unwrap()
          await doSearchRoles()
          void message.success('Role Deleted')
        },
      })
    },
    [dispatch, message, modal, doSearchRoles],
  )

  const onSetPermissionsClick = useCallback(
    function (record: TableData) {
      setRoleInSetting(roles.find((role) => role.id === record.key))
    },
    [roles],
  )

  const columns = useRoleSettingsColumns({
    onStatusChange,
    onSetPermissionsClick,
    onEditClick,
    onDeleteClick,
  })

  if (roleInSetting) {
    return (
      <SetPermissions
        role={roleInSetting}
        onBack={() => setRoleInSetting(null)}
        onSave={() => doSearchRoles()}
      />
    )
  }

  return (
    <Flex vertical className={styles.settingsSection}>
      <Flex className="settings-content-header">
        <b className="settings-header-title">Role settings</b>
        <Space>
          <Button type="primary" onClick={openCreateModal}>
            Add Role
          </Button>
        </Space>
      </Flex>
      <div className="settings-content">
        <SearchRole onSearch={doSearchRoles} />
        <div className="search-result">
          <Table<TableData>
            loading={loading}
            columns={columns}
            dataSource={tableData}
            pagination={{
              position: ['bottomLeft'],
              current: pagingQuery?.pageNum,
              pageSize: pagingQuery?.pageSize,
              total: pagingRoles?.total,
              onChange(pageNum, pageSize) {
                setPagingQuery({ pageNum, pageSize })
              },
            }}
          ></Table>
        </div>
      </div>
      {updateFormModal}
      {createFormModal}
    </Flex>
  )
}
